import { useEffect, useMemo, useState } from "react";
import {
  Grid,
  Box,
  Typography,
  Divider,
  CircularProgress,
} from "@mui/material";
import {
  Colors,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Chart,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";

// import { FiCurveInput } from "../../inputs/inputs";
import TripleABenchmark from "./triple-a-benchmark";
import USTreasuryYield from "./us-treasury-yield";
import useAAABenchmarkData from "./useAAABenchmarkData";
import useUSTreasuryData from "./useUSTreasuryData";

Chart.register(
  Colors,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
);

const COLORS = {
  AAA: {
    borderColor: "rgb(155, 168, 232)",
    backgroundColor: "rgb(155, 168, 232, 0.5)",
  },
  UST: {
    borderColor: "rgb(0, 68, 104)",
    backgroundColor: "rgb(0, 68, 104, 0.5)",
  },
  FHLB: {
    borderColor: "rgb(64, 138, 127)",
    backgroundColor: "rgb(64, 138, 127, 0.5)",
  },
};

export default function NationalBenchMarks({
  handleInputUpdate,
  savedInputValues,
}) {
  const [taxRate, setTaxRate] = useState(savedInputValues?.taxRate ?? 21);

  useEffect(() => {
    handleInputUpdate(taxRate);
  }, [taxRate])

  const {
    data: AAAData,
    newestRawData: rawAAAData,
    referenceDate: AAADate,
    loading: AAALoading,
  } = useAAABenchmarkData({ taxRate });
  const {
    data: treasuryData,
    newestRawData: rawTreasuryData,
    referenceDate: treasuryDate,
    loading: treasuryLoading,
  } = useUSTreasuryData();

  const chartData = useMemo(() => {
    // Populate Treasury data
    let treasuryDataInnerIndex = 0;
    const treasuryChartData = rawTreasuryData.length
      ? new Array(30).fill(null).map((_, index) => {
          if ([1, 2, 3, 5, 7, 10, 20, 30].includes(index + 1)) {
            return +rawTreasuryData[++treasuryDataInnerIndex + 3];
          }
          return null;
        })
      : [];

    // Populate AAA data
    const AAAChartData = rawAAAData
      .toSpliced(0, 1)
      .reduce((data, value) => [...data, +value], []);

    return {
      labels: [
        1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20,
        21, 22, 23, 24, 25, 26, 27, 28, 29, 30,
      ],
      datasets: [
        {
          label: "AAA",
          data: AAAChartData,
          ...COLORS["AAA"],
        },
        {
          label: "Treasury",
          data: treasuryChartData,
          ...COLORS["UST"],
        },
      ],
    };
  }, [rawAAAData, rawTreasuryData]);

  return (
    <Box>
      <Box mb={1}>
        <Typography textAlign="center" fontWeight="bold">
          National Benchmarks
        </Typography>
      </Box>
      <Divider />
      <Grid container spacing={4} pt={2}>
        <Grid item flexGrow={1}>
          <TripleABenchmark
            taxRate={taxRate}
            setTaxRate={setTaxRate}
            data={AAAData}
            loading={AAALoading}
            referenceDate={AAADate}
          />
        </Grid>

        <Grid item flexGrow={1}>
          <USTreasuryYield
            data={treasuryData}
            loading={treasuryLoading}
            referenceDate={treasuryDate}
          />
        </Grid>
        <Grid item sx={{ width: "100%" }}>
          <Box
            sx={{ px: 2 }}
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 350,
            }}
          >
            {treasuryLoading || AAALoading? (
              <CircularProgress />
            ) : (
              <Line
                data={chartData}
                options={{
                  maintainAspectRatio: false,
                  spanGaps: true,
                  scales: {
                    y: {
                      title: { text: "Yield", display: true },
                      ticks: {
                        callback: (v) => `${v.toFixed(2)}%`,
                      },
                    },
                    x: { title: { text: "Maturity", display: true } },
                  },
                  plugins: {
                    colors: {
                      enabled: true,
                    },
                    tooltip: {
                      callbacks: {
                        label: (context) =>
                          `${context.dataset.label}: ${context.raw.toFixed(
                            2
                          )}%`,
                        title: ([context]) =>
                          `Maturity: ${context.label} ${
                            context.label === "1" ? "Yr" : "Yrs"
                          }`,
                      },
                    },
                  },
                }}
              />
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
